import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Search } from "../shared/Icon";
import { Kbd } from "../shared/Kbd";
import { cn } from "../../lib/cn";
import { search as searchApi } from "../../lib/api";
import { useUI } from "../../lib/store";
import { StatusDot, statusLabel } from "../shared/StatusDot";
import type { SearchHit } from "../../lib/types";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: Props) {
  const projectId = useUI((s) => s.selectedProjectId);
  const setSelectedTaskId = useUI((s) => s.setSelectedTaskId);
  const setRightPaneMode = useUI((s) => s.setRightPaneMode);
  const setMainView = useUI((s) => s.setMainView);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const { data, isFetching } = useQuery({
    queryKey: ["search", query, projectId],
    queryFn: () =>
      query.length >= 2
        ? searchApi.query({ q: query, project_id: projectId ?? undefined, limit: 12 })
        : Promise.resolve({ query, hits: [] as SearchHit[], took_ms: 0 }),
    enabled: open,
    staleTime: 1500,
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const selectHit = (hit: SearchHit) => {
    setSelectedTaskId(hit.task_id);
    setMainView("board");
    setRightPaneMode("activity");
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="absolute inset-0 bg-[rgb(0_0_0_/_0.45)] backdrop-blur-[2px]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              "relative w-full max-w-[640px] bg-bg-surface",
              "rounded-lg shadow-lg border border-border-subtle",
              "flex flex-col overflow-hidden",
            )}
          >
            <div className="flex items-center gap-2 px-3 h-12 border-b border-border-subtle">
              <Search className="text-text-tertiary" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search across every task transcript…"
                className={cn(
                  "flex-1 bg-transparent outline-none",
                  "text-body text-text-primary placeholder:text-text-quaternary",
                )}
              />
              {isFetching && (
                <span className="text-mono text-text-quaternary">…</span>
              )}
              <Kbd>esc</Kbd>
            </div>
            <div className="max-h-[480px] overflow-y-auto">
              {query.length < 2 ? (
                <div className="px-3 py-6 text-center text-small text-text-tertiary">
                  Type at least two characters.
                </div>
              ) : data?.hits.length === 0 ? (
                <div className="px-3 py-6 text-small text-text-tertiary">
                  No matches.
                </div>
              ) : (
                <ul>
                  {data?.hits.map((hit, i) => (
                    <li key={hit.task_id}>
                      <button
                        onClick={() => selectHit(hit)}
                        onMouseEnter={() => setActiveIndex(i)}
                        className={cn(
                          "w-full text-left px-3 py-2.5 flex items-start gap-3",
                          i === activeIndex
                            ? "bg-bg-inset"
                            : "hover:bg-bg-inset/60",
                        )}
                      >
                        <StatusDot status={hit.status} className="mt-1" />
                        <div className="flex-1 min-w-0">
                          <div className="text-body text-text-primary truncate">
                            {hit.title}
                          </div>
                          <div
                            className="text-mono text-text-tertiary truncate"
                            dangerouslySetInnerHTML={{ __html: hit.snippet }}
                          />
                        </div>
                        <span className="text-mono text-text-quaternary">
                          {statusLabel(hit.status)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-t border-border-subtle px-3 py-2 flex items-center gap-3 text-mono text-text-quaternary">
              <span className="ml-auto">{data?.took_ms ? `${data.took_ms}ms` : ""}</span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
